// cohorts/create: what a failed create call means. Pure, so it is tested in plain Node.
import { isApiError } from "@core/errors";

/**
 * Why creating the cohort failed, in the terms the form acts on (cohorts-v1.md):
 *   - `400 invalid_name`: the name is blank or too long.
 *   - `400 program_end_must_follow_start` (or a `program_end_date` field error): the end is not
 *     after the start.
 *   - `409 active_administrator_required`: the chosen account is no longer an active Administrator.
 *   - `409 idempotency_key_conflict`: this key was used with another payload; draw a new key.
 *   - `403 permission_denied`: not the President (the button should not have been there).
 */
export type CreateFailure =
  | { kind: "name_invalid" }
  | { kind: "end_before_start" }
  | { kind: "administrator_unavailable" }
  | { kind: "key_conflict" }
  | { kind: "forbidden" }
  | { kind: "rate_limited" }
  | { kind: "network" }
  | { kind: "unknown" };

export function classifyCreateFailure(error: unknown): CreateFailure {
  if (!isApiError(error)) return { kind: "unknown" };
  if (error.status === 0) return { kind: "network" };
  if (error.status === 429) return { kind: "rate_limited" };
  if (error.status === 403) return { kind: "forbidden" };

  if (error.status === 409) {
    if (error.code === "active_administrator_required") {
      return { kind: "administrator_unavailable" };
    }
    if (error.code === "idempotency_key_conflict") {
      return { kind: "key_conflict" };
    }
  }

  if (error.status === 400) {
    if (error.code === "invalid_name" || error.fieldErrors?.name) {
      return { kind: "name_invalid" };
    }
    if (
      error.code === "program_end_must_follow_start" ||
      error.fieldErrors?.program_end_date
    ) {
      return { kind: "end_before_start" };
    }
  }

  return { kind: "unknown" };
}
